import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from './firebase';

// Full-screen intro carousel shown once, right after a new account is created
// (App.js routes create-profile -> /onboarding -> /me). It pitches the features
// that actually retain people: adding non-users, move notifications, the tile
// speed dial, and the home-screen widget. Finishing sets a separate
// `yt8_intro_seen` flag so it never re-runs but does NOT touch onboardedAt, which
// still gates the in-flow add coach that guides the first Top 8 build.
const INTRO_FLAG = 'yt8_intro_seen';

const CARDS = [
  { h: 'Add anyone', p: "Even people who aren't on the app. Invite them and they slot right in when they join." },
  { h: 'Know where you stand', p: 'Get a notification the second someone moves you up or down their Top 8.' },
  { h: 'Every tile is a speed dial', p: 'Send a text; check their LinkedIn; watch their latest TikTok... your call!' },
  { h: 'One tap away', p: 'Drop the Top 8 widget on your home screen.' },
];

function Slide({ i }) {
  if (i === 0) return (
    <div className="ob-grid2">
      <div className="ob-tile"><div className="ob-rank">1</div><div className="ob-ph" style={{ background: 'var(--accent)' }}>M</div><div className="ob-lab"><div className="ob-nm">Maya</div><div className="ob-ac">Snapchat</div></div></div>
      <div className="ob-tile"><div className="ob-rank">2</div><div className="ob-ph" style={{ background: 'var(--accent-2)' }}>J</div><div className="ob-lab"><div className="ob-nm">Jordan</div><div className="ob-ac">Text</div></div></div>
      <div className="ob-tile ob-prospect"><div className="ob-inv">✉</div><div className="ob-ph">D</div><div className="ob-lab"><div className="ob-nm">Dre</div><div className="ob-ac">Not on the app</div></div></div>
      <div className="ob-tile ob-add"><span className="ob-plus">+</span><span>Add</span></div>
    </div>
  );
  if (i === 1) return (
    <div className="ob-notis">
      <div className="ob-noti"><div className="ob-ico">Y8</div><div><div className="ob-t1"><b>Alex</b> moved you <span className="ob-up">▲ up</span> to <b>#2</b></div><div className="ob-t2">now</div></div></div>
      <div className="ob-noti"><div className="ob-ico">Y8</div><div><div className="ob-t1"><b>Sam</b> moved you <span className="ob-dn">▼ down</span> to <b>#6</b></div><div className="ob-t2">2m ago</div></div></div>
      <div className="ob-noti"><div className="ob-ico">Y8</div><div><div className="ob-t1"><b>Priya</b> added you to their Top 8</div><div className="ob-t2">1h ago</div></div></div>
    </div>
  );
  if (i === 2) return (
    <div className="ob-sd">
      <div className="ob-htile"><div className="ob-hph" style={{ background: 'var(--accent)' }}>M</div><div className="ob-hnm">Maya</div></div>
      <div className="ob-menu">
        <div className="ob-row ob-sel"><span className="ob-ri" style={{ background: 'var(--good)' }}>T</span> Text message <span className="ob-chk">✓</span></div>
        <div className="ob-row"><span className="ob-ri" style={{ background: '#0a66c2', color: '#fff' }}>in</span> LinkedIn</div>
        <div className="ob-row"><span className="ob-ri" style={{ background: '#000', color: '#fff' }}>♪</span> TikTok</div>
      </div>
    </div>
  );
  return (
    <div className="ob-home">
      <div className="ob-widget">
        <div className="ob-wh">★ Your Top 8</div>
        <div className="ob-wgrid">
          {[['var(--accent)', 'M'], ['var(--accent-2)', 'J'], ['var(--good)', 'A'], ['#e1306c', 'P'], ['#f0997b', 'D'], ['#5dcaa5', 'S'], ['#f4c065', 'K'], ['#85b7eb', 'R']].map(([c, l], n) => (
            <div key={n} className="ob-wcell" style={{ background: c }}>{l}<span className="ob-wr">{n + 1}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function OnboardingPage({ currentUser, currentUserProfile }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(0);
  const sx = useRef(null);
  const last = CARDS.length - 1;

  const canAddWidget = typeof window !== 'undefined'
    && window.YT8Native && typeof window.YT8Native.promptAddWidget === 'function';

  const go = (i) => setIdx(Math.max(0, Math.min(last, i)));

  const finish = async () => {
    try { localStorage.setItem(INTRO_FLAG, '1'); } catch {}
    if (currentUser?.uid) {
      try { await updateDoc(doc(db, 'users', currentUser.uid), { introSeenAt: new Date() }); } catch {}
    }
    navigate('/me', { replace: true });
  };

  const onTouchStart = (e) => { sx.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (sx.current === null) return;
    const dx = e.changedTouches[0].clientX - sx.current;
    if (Math.abs(dx) > 45) go(idx + (dx < 0 ? 1 : -1));
    sx.current = null;
  };

  return (
    <div className="ob-wrap" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
      <style>{OB_CSS}</style>
      <button className="ob-skip" onClick={finish}>Skip</button>

      <div className="ob-visual"><Slide i={idx} /></div>

      <div className="ob-cap">
        <h2>{CARDS[idx].h}</h2>
        <p>{CARDS[idx].p}</p>
        {idx === last && !canAddWidget && (
          <div className="ob-pill">Get it on Android (Apple coming soon!)</div>
        )}
      </div>

      <div className="ob-dots">
        {CARDS.map((_, i) => (
          <button key={i} className={'ob-dot' + (i === idx ? ' on' : '')} aria-label={`Go to slide ${i + 1}`} onClick={() => go(i)} />
        ))}
      </div>

      <div className="ob-cbar">
        <button className="btn btn-ghost" style={{ visibility: idx === 0 ? 'hidden' : 'visible' }} onClick={() => go(idx - 1)}>Back</button>
        <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => (idx < last ? go(idx + 1) : finish())}>
          {idx < last ? 'Next' : 'Build your Top 8'}
        </button>
      </div>
    </div>
  );
}

const OB_CSS = `
.ob-wrap{ position:fixed; inset:0; background:var(--bg); color:var(--text); display:flex; flex-direction:column;
  padding:max(44px,env(safe-area-inset-top)) 22px max(24px,env(safe-area-inset-bottom)); z-index:1000; }
.ob-skip{ position:absolute; top:max(40px,env(safe-area-inset-top)); right:18px; background:none; border:none; color:var(--muted);
  font:inherit; font-size:14px; cursor:pointer; padding:6px 8px; }
.ob-skip:hover{ color:var(--text); }
.ob-visual{ flex:1; display:flex; align-items:center; justify-content:center; min-height:0; }
.ob-cap{ text-align:center; }
.ob-cap h2{ font-size:24px; font-weight:800; letter-spacing:-.02em; margin:14px 0 8px; }
.ob-cap p{ font-size:15px; line-height:1.5; color:var(--muted); margin:0 auto; max-width:300px; }
.ob-pill{ display:inline-block; margin-top:14px; background:rgba(106,166,255,.12); border:1px solid rgba(106,166,255,.3);
  color:#bcd6ff; font-size:12px; font-weight:600; padding:7px 13px; border-radius:99px; }
.ob-dots{ display:flex; gap:7px; justify-content:center; padding:22px 0; }
.ob-dot{ width:7px; height:7px; border-radius:99px; background:var(--border-hi); border:none; padding:0; cursor:pointer; transition:all .2s; }
.ob-dot.on{ width:22px; background:var(--accent); }
.ob-cbar{ display:flex; gap:10px; align-items:center; max-width:440px; width:100%; margin:0 auto; }
.ob-grid2{ display:grid; grid-template-columns:1fr 1fr; gap:13px; width:100%; max-width:280px; }
.ob-tile{ background:var(--card-2); border:1px solid var(--border); border-radius:14px; overflow:hidden; position:relative; }
.ob-ph{ aspect-ratio:1; display:flex; align-items:center; justify-content:center; font-size:34px; font-weight:800; color:#0d0d0f; }
.ob-lab{ padding:8px 9px 9px; }
.ob-nm{ font-size:13px; font-weight:600; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.ob-ac{ font-size:11px; color:var(--muted); margin-top:1px; }
.ob-rank{ position:absolute; top:6px; left:6px; width:19px; height:19px; border-radius:99px; background:rgba(0,0,0,.55);
  color:#fff; font-size:10px; font-weight:700; display:flex; align-items:center; justify-content:center; }
.ob-prospect .ob-ph{ background:var(--card); color:var(--muted); font-size:30px; border-bottom:1px dashed var(--border-hi); }
.ob-inv{ position:absolute; top:6px; right:6px; width:21px; height:21px; border-radius:99px; background:var(--accent);
  color:#0d0d0f; display:flex; align-items:center; justify-content:center; font-size:11px; }
.ob-add{ border-style:dashed; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:4px;
  color:var(--accent); font-size:13px; font-weight:600; }
.ob-plus{ font-size:30px; line-height:1; }
.ob-notis{ display:flex; flex-direction:column; gap:11px; width:100%; max-width:300px; }
.ob-noti{ background:var(--card); border:1px solid var(--border); border-radius:14px; padding:12px 13px; display:flex; gap:11px; align-items:flex-start; }
.ob-ico{ width:32px; height:32px; border-radius:9px; background:var(--accent); color:#0d0d0f; flex-shrink:0;
  display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:800; }
.ob-t1{ font-size:13px; line-height:1.4; }
.ob-t2{ font-size:11px; color:var(--muted); margin-top:3px; }
.ob-up{ color:var(--good); font-weight:700; }
.ob-dn{ color:var(--bad); font-weight:700; }
.ob-sd{ width:100%; max-width:280px; }
.ob-htile{ width:104px; margin:0 auto 16px; }
.ob-hph{ aspect-ratio:1; border-radius:15px; display:flex; align-items:center; justify-content:center; font-size:38px; font-weight:800; color:#0d0d0f; }
.ob-hnm{ text-align:center; font-size:14px; font-weight:600; margin-top:8px; }
.ob-menu{ background:var(--card); border:1px solid var(--border); border-radius:14px; overflow:hidden; }
.ob-row{ display:flex; align-items:center; gap:11px; padding:13px 14px; font-size:14px; border-bottom:1px solid var(--border); }
.ob-row:last-child{ border-bottom:none; }
.ob-ri{ width:24px; height:24px; border-radius:7px; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:800; color:#0d0d0f; flex-shrink:0; }
.ob-sel{ background:rgba(106,166,255,.10); }
.ob-chk{ margin-left:auto; color:var(--accent); font-weight:700; }
.ob-home{ width:100%; max-width:270px; background:linear-gradient(160deg,#243049,#1a1330); border-radius:22px; padding:18px; border:1px solid var(--border); }
.ob-widget{ background:rgba(13,13,17,.82); border-radius:18px; padding:13px; }
.ob-wh{ font-size:12px; font-weight:700; color:#fff; margin-bottom:10px; }
.ob-wgrid{ display:grid; grid-template-columns:repeat(4,1fr); gap:8px; }
.ob-wcell{ aspect-ratio:1; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:14px; font-weight:800; color:#0d0d0f; position:relative; }
.ob-wr{ position:absolute; bottom:-1px; right:-1px; width:14px; height:14px; border-radius:99px; background:#0d0d17; color:#fff; font-size:8px; display:flex; align-items:center; justify-content:center; border:1px solid #2a2a3a; }
`;
